import * as ExcelJS from 'exceljs';
import { BaseExcelService } from './baseExcelService';
import { MultiWithdrawalExportData, ExcelColors } from './types';

/**
 * Service for exporting multiple withdrawal transactions to Excel
 */
export class MultiWithdrawalExportService {
  /**
   * Export multiple withdrawal transactions to Excel summary report
   */
  static async exportToExcel(data: MultiWithdrawalExportData): Promise<void> {
    const { records, month, year } = data;

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Báo cáo rút tiền');

    // Set column widths
    worksheet.columns = [
      { width: 25 }, // A - Tên khách hàng
      { width: 15 }, // B - Số thẻ
      { width: 20 }, // C - Chủ thẻ
      { width: 20 }, // D - Ngân hàng
      { width: 15 }, // E - Số tiền rút
      { width: 12 }, // F - Phí KH
      { width: 12 }, // G - Phí POS (%)
      { width: 15 }, // H - Thành tiền
      { width: 12 }, // I - Phí CTV (%)
      { width: 15 }, // J - HH
      { width: 15 }, // K - Ngày GD
    ];

    // 1. Main title
    worksheet.mergeCells('A1:K1');
    BaseExcelService.createHeaderCell(
      worksheet,
      'A1',
      'BÁO CÁO TỔNG HỢP DỊCH VỤ RÚT TIỀN',
      16,
      ExcelColors.PRIMARY_HEADER
    );

    // 2. Report period and info
    MultiWithdrawalExportService.addReportInfo(
      worksheet,
      month,
      year,
      records.length
    );

    // 3. Table header
    const startRow = 7;
    const headers = [
      'Khách hàng',
      'Số thẻ',
      'Chủ thẻ',
      'Ngân hàng',
      'Số tiền rút',
      'Phí KH',
      'Phí POS (%)',
      'Thành tiền',
      'Phí CTV (%)',
      'HH',
      'Ngày GD',
    ];
    BaseExcelService.createTableHeader(worksheet, startRow, headers);

    // 4. Data rows and calculate totals
    const totals = MultiWithdrawalExportService.addDataRows(
      worksheet,
      records,
      startRow + 1
    );

    // 5. Summary section
    MultiWithdrawalExportService.addSummarySection(
      worksheet,
      records.length,
      totals,
      startRow + records.length + 2
    );

    // Generate and save file
    const fileName = BaseExcelService.generateFileName(
      'BaoCaoRutTien',
      `Thang${month}_${year}`
    );
    await BaseExcelService.saveExcelFile(workbook, fileName);
  }

  /**
   * Add report period and basic information
   */
  private static addReportInfo(
    worksheet: ExcelJS.Worksheet,
    month: number,
    year: number,
    recordCount: number
  ): void {
    // Report period
    worksheet.getCell('A3').value = 'Kỳ báo cáo:';
    worksheet.getCell('A3').font = { bold: true };
    worksheet.getCell('B3').value = `Tháng ${month}/${year}`;

    // Export date
    worksheet.getCell('A4').value = 'Ngày xuất:';
    worksheet.getCell('A4').font = { bold: true };
    worksheet.getCell('B4').value = BaseExcelService.formatVietnameseDate(
      new Date()
    );

    // Record count
    worksheet.getCell('A5').value = 'Số giao dịch:';
    worksheet.getCell('A5').font = { bold: true };
    worksheet.getCell('B5').value = `${recordCount} giao dịch`;
  }

  /**
   * Add data rows and calculate totals
   */
  private static addDataRows(
    worksheet: ExcelJS.Worksheet,
    records: any[],
    startRow: number
  ): {
    totalAmount: number;
    totalFeeForCustomer: number;
    totalThanhTien: number;
    totalFeeApplied: number;
    totalCommissionCTV: number;
    totalHH: number;
  } {
    const totals = {
      totalAmount: 0,
      totalFeeForCustomer: 0,
      totalThanhTien: 0,
      totalFeeApplied: 0,
      totalCommissionCTV: 0,
      totalHH: 0,
    };

    records.forEach((record, index) => {
      const row = startRow + index;

      // Extract basic data
      const customerName = record.creditCard?.customer?.name || 'N/A'; // Tên khách hàng
      const cardNumber = record.creditCard?.cardNumber?.slice(-4) || 'N/A';
      const cardHolderName = record.creditCard?.cardHolderName || 'N/A'; // Chủ thẻ
      // Use updated backend structure for bank name
      const bankName =
        record.creditCard?.bank?.name ||
        record.bankName ||
        record.creditCard?.bankName ||
        'N/A';
      const amount = record.amount || 0;
      const feeForCustomer = record.feeForCustomer || 0;
      const customerType = record.customerType || '%';
      const feeApplied = record.feeApplied || 0;
      const commissionRateForPartner = record.commissionRateForPartner || 0;
      const transactionDate = new Date(
        record.transactionDate
      ).toLocaleDateString('vi-VN');

      // Calculate thành tiền
      let thanhTien = 0;
      if (feeForCustomer) {
        if (customerType === 'vn') {
          thanhTien = feeForCustomer;
        } else {
          thanhTien = amount * (feeForCustomer / 100);
        }
      }

      // Calculate HH based on customer fee type
      let hh = 0;
      if (customerType === 'vn') {
        // Phí KH là VND: HH = phí thu - (phí CTV% * Rút)
        hh = feeForCustomer - (commissionRateForPartner / 100) * amount;
      } else {
        // Phí KH là %: HH = (phí thu% * Rút) - (phí CTV% * Rút)
        hh =
          (feeForCustomer / 100) * amount -
          (commissionRateForPartner / 100) * amount;
      }

      // Format phí KH display
      let feeKHDisplay = '';
      if (customerType === 'vn') {
        feeKHDisplay = feeForCustomer.toLocaleString('vi-VN') + ' VND';
      } else {
        feeKHDisplay = `${feeForCustomer}%`;
      }

      // Update totals
      totals.totalAmount += amount;
      totals.totalFeeForCustomer += feeForCustomer;
      totals.totalThanhTien += thanhTien;
      totals.totalFeeApplied += feeApplied; // POS fee percentage
      totals.totalCommissionCTV += commissionRateForPartner; // CTV fee percentage
      totals.totalHH += hh;

      const rowData = [
        customerName, // A - Khách hàng
        `***${cardNumber}`, // B - Số thẻ
        cardHolderName, // C - Chủ thẻ
        bankName, // D - Ngân hàng
        amount, // E - Số tiền rút
        feeKHDisplay, // F - Phí KH
        `${feeApplied}%`, // G - Phí POS (%)
        thanhTien, // H - Thành tiền
        `${commissionRateForPartner}%`, // I - Phí CTV (%)
        hh, // J - HH
        transactionDate, // K - Ngày GD
      ];

      rowData.forEach((cellData, colIndex) => {
        const cell = worksheet.getCell(row, colIndex + 1);
        cell.value = cellData;

        // Format currency columns (Số tiền rút, Thành tiền, HH)
        if ([4, 7, 9].includes(colIndex) && typeof cellData === 'number') {
          BaseExcelService.formatCurrencyCell(cell, cellData);
        }
        // Apply border for other cells
        else {
          BaseExcelService.applyBorder(cell);
        }
      });
    });

    return totals;
  }

  /**
   * Add summary section with totals
   */
  private static addSummarySection(
    worksheet: ExcelJS.Worksheet,
    recordCount: number,
    totals: {
      totalAmount: number;
      totalFeeForCustomer: number;
      totalThanhTien: number;
      totalFeeApplied: number;
      totalCommissionCTV: number;
      totalHH: number;
    },
    startRow: number
  ): void {
    // Summary header
    worksheet.mergeCells(`A${startRow}:K${startRow}`);
    BaseExcelService.createHeaderCell(
      worksheet,
      `A${startRow}`,
      'TỔNG KẾT',
      12,
      ExcelColors.SECONDARY_HEADER
    );

    const summaryData = [
      ['Tổng số giao dịch:', recordCount],
      ['Tổng tiền rút:', totals.totalAmount],
      ['Tổng thành tiền:', totals.totalThanhTien],
      ['Tổng HH:', totals.totalHH],
    ];

    summaryData.forEach((data, index) => {
      const row = startRow + 1 + index;

      // Label column
      const labelCell = worksheet.getCell(`A${row}`);
      labelCell.value = data[0];
      labelCell.font = { bold: true };

      // Value column
      const valueCell = worksheet.getCell(`B${row}`);
      valueCell.value = data[1];

      if (typeof data[1] === 'number') {
        BaseExcelService.formatCurrencyCell(valueCell, data[1]);
      }

      // Highlight final total
      if (index === summaryData.length - 1) {
        BaseExcelService.applyCellStyle(labelCell, {
          font: { bold: true, color: { argb: ExcelColors.WHITE_TEXT } },
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: ExcelColors.PRIMARY_HEADER },
          },
        });

        BaseExcelService.applyCellStyle(valueCell, {
          font: { bold: true, color: { argb: ExcelColors.WHITE_TEXT } },
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: ExcelColors.PRIMARY_HEADER },
          },
        });
      }
    });
  }
}
